/* `t` para los pocos mensajes que este módulo arma por su cuenta: los
 * errores de red, que nacen ACÁ y nunca vienen del servidor. No hay
 * ciclo — `i18n.js` no importa nada de este archivo. */
import { t, idiomaActual } from "./i18n";

// Capa de acceso al backend.
//
// Todo el manejo de sesión vive acá: guardar el token, mandarlo en cada
// request, y renovarlo cuando vence. Los componentes no saben que existe
// un token — solo llaman funciones.
//
// El access token dura una hora. Cuando el backend responde 401 se
// intenta renovar con el refresh token y se repite el request una sola
// vez. Si tampoco funciona, se cierra sesión.

// A dónde vive el backend.
//
// En producción es el MISMO servidor que sirvió esta página: Express
// entrega el build de React y la API bajo la misma URL, así que basta
// con rutas relativas y el navegador nunca cruza de origen.
//
// En desarrollo son dos procesos: React en :3000 y el backend en :3001.
// Se usa `window.location.hostname` y no "localhost" para poder abrir
// la app desde el celular en la misma WiFi.
//
// REACT_APP_API_URL pisa todo, para apuntar a un backend desplegado
// desde una compilación local.
export const API_URL =
  process.env.REACT_APP_API_URL ??
  (process.env.NODE_ENV === "production"
    ? "" // mismo origen
    : `http://${window.location.hostname}:3001`);

const CLAVE_SESION = "armario_sesion";

/* ------------------------------------------------------------------
   Sesión en localStorage
------------------------------------------------------------------ */

export function leerSesion() {
  try {
    const crudo = localStorage.getItem(CLAVE_SESION);
    return crudo ? JSON.parse(crudo) : null;
  } catch {
    return null;
  }
}

export function guardarSesion(sesion) {
  localStorage.setItem(CLAVE_SESION, JSON.stringify(sesion));
}

export function borrarSesion() {
  localStorage.removeItem(CLAVE_SESION);
}

/* ------------------------------------------------------------------
   Request con renovación automática
------------------------------------------------------------------ */

// Evita que varios requests en paralelo disparen varios refresh a la vez
let refrescando = null;

/* ------------------------------------------------------------------
   AVISO DE SESIÓN MUERTA

   EL BUG QUE ESTO ARREGLA (2026-08-24, CR-8)

   Cuando el refresh fallaba, esto hacía `borrarSesion()` y tiraba "Tu
   sesión venció. Entrá de nuevo." — y NADIE MÁS SE ENTERABA.

   AuthContext seguía con `usuario` seteado, así que la app entera
   quedaba montada: pestañas, armario, botones. Todo visible, todo
   respondiendo con el mismo error, y sin ningún camino de vuelta al
   login. La única salida era recargar la página a mano, perdiendo lo
   que estuvieras haciendo.

   Peor en la carga por lotes: el bucle de tandas atrapaba el error y
   SEGUÍA subiendo megabytes de fotos que iban a fallar seguro, una por
   una, con el usuario mirando fichas rojas aparecer.

   Un aviso y no una redirección: quién decide qué hacer con una sesión
   muerta es la app, no la capa de red. Acá solo se avisa.
------------------------------------------------------------------ */

const oyentesDeSesion = new Set();

/** Se llama cuando la sesión murió y no se pudo renovar. */
export function alMorirLaSesion(fn) {
  oyentesDeSesion.add(fn);
  return () => oyentesDeSesion.delete(fn); // para desuscribirse al desmontar
}

function avisarSesionMuerta() {
  for (const fn of oyentesDeSesion) {
    try {
      fn();
    } catch {
      /* un oyente roto no puede impedir que se enteren los demás */
    }
  }
}

/** ¿Este error es "se te venció la sesión"? */
export function esSesionVencida(e) {
  return e?.sesionVencida === true || e?.status === 401;
}

async function renovarSesion() {
  const sesion = leerSesion();
  if (!sesion?.refresh_token) return null;

  // Si ya hay un refresh en curso, esperar ese en vez de lanzar otro
  if (refrescando) return refrescando;

  refrescando = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: sesion.refresh_token }),
      });

      /* ⚠️  UN 429 NO ES UNA SESIÓN MUERTA (auditoría 2026-09-15, §2.2).
       *
       * Esto hacía `if (!res.ok) return null`, y `pedir()` traducía ese
       * null a «tu sesión venció»: borraba la sesión y mandaba al
       * login. Un 429 del freno, un 502 del proxy o un corte de red
       * recibían el mismo trato que un refresh token inválido. Con
       * tokens de una hora y un freno por IP, eso era deslogueo masivo
       * detrás de cualquier NAT.
       *
       * Ahora se distingue: solo 401 y 400 son «este token no sirve».
       * Todo lo demás es transitorio: se devuelve la marca, `pedir()`
       * tira un error de red, y la sesión sigue guardada para el
       * próximo intento. */
      if (res.status === 401 || res.status === 400) return null;
      if (!res.ok) return { transitorio: true, status: res.status };

      const data = await res.json();
      guardarSesion(data.sesion);
      return data.sesion;
    } catch {
      return { transitorio: true, status: 0 };
    } finally {
      refrescando = null;
    }
  })();

  return refrescando;
}

async function pedir(ruta, opciones = {}, reintento = true) {
  const sesion = leerSesion();

  const headers = {
    "Content-Type": "application/json",
    ...(opciones.headers || {}),
  };

  if (sesion?.access_token) {
    headers.Authorization = `Bearer ${sesion.access_token}`;
  }

  let res;
  try {
    res = await fetch(`${API_URL}${ruta}`, { ...opciones, headers });
  } catch {
    // El mensaje viejo —"¿Está corriendo el backend?"— era para el
    // dueño desarrollando en su máquina, no para 30.000 usuarios que no
    // saben qué es un backend.
    const e = new Error(t("comun.sinRed", "Sin conexión con el servidor. Prueba de nuevo."));
    e.sinRed = true;
    throw e;
  }

  // Token vencido: renovar y repetir una sola vez
  if (res.status === 401 && reintento && sesion?.refresh_token) {
    const nueva = await renovarSesion();
    if (nueva && nueva.transitorio) {
      /* El refresh no se pudo hacer AHORA, no es que no se pueda. La
       * sesión se conserva; el pedido falla como falla un corte de
       * red, y el que lo hizo lo reintenta cuando quiera. */
      const e = new Error(t("comun.sinRed", "Sin conexión con el servidor. Prueba de nuevo."));
      e.sinRed = true;
      e.status = nueva.status;
      throw e;
    }
    if (nueva) return pedir(ruta, opciones, false);

    borrarSesion();
    avisarSesionMuerta();
    const e = new Error(t("comun.sesionVencida", "Tu sesión venció. Entra de nuevo."));
    // Marcado para que quien esté en medio de un trabajo largo —la
    // carga por lotes— pueda cortar en vez de seguir intentando.
    e.sesionVencida = true;
    e.status = 401;
    throw e;
  }

  /* El parse va en try (2026-08-24, ME-9).
   *
   * Un 502 o un 504 del proxy devuelven HTML, no JSON. `JSON.parse` de
   * "<html>..." tira `SyntaxError: Unexpected token '<'`, y ESE era el
   * mensaje que llegaba a la pantalla del usuario. Ahora se cae al
   * status, que al menos es cierto.
   */
  const texto = await res.text();
  let data = null;
  if (texto) {
    try {
      data = JSON.parse(texto);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    // El mensaje va en `message`, como siempre. Pero además se le
    // cuelgan el STATUS y el CUERPO entero.
    //
    // POR QUÉ: hay respuestas donde el status ES la información. El 409
    // de "este color está en uso en 12 prendas" no es un error — es la
    // pregunta que hay que hacer antes de borrar, y quien la recibe
    // necesita distinguirla de un 400 de verdad. Sin esto, la única
    // forma era un regex sobre el texto del mensaje, que se rompe el
    // día que alguien mejora la redacción.
    const e = new Error(data?.error || `Error ${res.status}`);
    e.status = res.status;
    if (data && typeof data === "object") Object.assign(e, data);
    throw e;
  }

  return data;
}

/* ------------------------------------------------------------------
   Auth
------------------------------------------------------------------ */

export async function registrarse(email, password, nombre, idioma) {
  const data = await pedir("/api/auth/signup", {
    method: "POST",
    /* `idioma` viaja en el alta y no se arregla después con un PATCH:
     * con la confirmación de correo activada el registro NO devuelve
     * sesión, así que no hay con qué hacer ese PATCH. El idioma que la
     * persona eligió antes de registrarse tiene que entrar con ella o
     * se pierde. El servidor lo valida contra la lista y lo deja en el
     * perfil que crea el trigger. */
    body: JSON.stringify({ email, password, nombre, idioma }),
  });

  if (data.sesion) guardarSesion(data.sesion);
  return data;
}

export async function entrar(email, password) {
  const data = await pedir("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

  guardarSesion(data.sesion);
  return data;
}

export async function salir() {
  try {
    await pedir("/api/auth/logout", { method: "POST" });
  } catch {
    // Si el backend no responde, igual se cierra sesión local
  }
  borrarSesion();
}

export function quienSoy() {
  /* El desfase horario del navegador viaja con el pedido.
   *
   * `estacionActual` lo calcula el servidor —para no duplicar allá la
   * tabla de meses y hemisferios— y el servidor corre en UTC. Sin este
   * dato, los cuatro días del año en que cambia la estación el mes que
   * ve el servidor no es el del usuario: hasta 13 horas de diferencia
   * en Nueva Zelanda. No falla nada, solo preselecciona la estación
   * equivocada. */
  return pedir(`/api/auth/me?tz=${new Date().getTimezoneOffset()}`);
}

/* ------------------------------------------------------------------
   Recuperación de contraseña
------------------------------------------------------------------ */

export function pedirReset(email) {
  return pedir("/api/auth/forgot", {
    method: "POST",
    body: JSON.stringify({ email, redirectTo: window.location.origin }),
  });
}

// Cambia la contraseña usando el token del link del mail.
// Ese token no es el de la sesión actual (puede no haber ninguna),
// así que se manda explícito en vez de tomarlo de localStorage.
export async function cambiarPassword(password, tokenDelLink) {
  const res = await fetch(`${API_URL}/api/auth/reset`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokenDelLink}`,
    },
    body: JSON.stringify({ password }),
  });

  const texto = await res.text();
  /* ⚠️  LA MISMA RED DE CONTENCIÓN QUE `pedir()`, Y NO ESTABA (2026-09-15).
   * Un 502 del proxy devuelve HTML; sin el try, la persona que está
   * recuperando su contraseña veía `SyntaxError: Unexpected token '<'`.
   * El guardián de test_api_cliente.js miraba el PRIMER
   * `res.text()` del archivo —el de pedir()— y daba verde. */
  let data = null;
  if (texto) {
    try {
      data = JSON.parse(texto);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    throw new Error(data?.error || t("comun.noSeCambioContrasena", "No se pudo cambiar la contraseña"));
  }

  return data;
}

/* ------------------------------------------------------------------
   Token del link de recuperación

   Supabase lo manda en el fragmento de la URL (#access_token=...) y no
   como query param. El fragmento no viaja al servidor, que es
   justamente lo que se quiere para un token.
------------------------------------------------------------------ */

export function leerTokenDeRecuperacion() {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;

  const params = new URLSearchParams(hash.slice(1));

  // Supabase avisa acá si el link venció o ya se usó
  const error = params.get("error_description") || params.get("error");
  if (error) return { error: decodeURIComponent(error.replace(/\+/g, " ")) };

  const token = params.get("access_token");
  const tipo = params.get("type");

  if (!token || tipo !== "recovery") return null;

  return { token };
}

// Saca el token de la barra de direcciones una vez leído, para que no
// quede en el historial ni se comparta al copiar el link.
export function limpiarHash() {
  window.history.replaceState(null, "", window.location.pathname);
}

// Quedaba de la Fase 1 apuntando a un PUT que nunca existió: cualquier
// llamada habría dado 404. Ahora apunta al PATCH real. La usa
// AuthContext; la pantalla de Perfil usa `guardarPerfil` directo.
export function actualizarPerfil(cambios) {
  return pedir("/api/perfil", {
    method: "PATCH",
    body: JSON.stringify(cambios),
  });
}

/* ------------------------------------------------------------------
   Armario
------------------------------------------------------------------ */

/**
 * Listado con filtros. Devuelve { prendas, total, totalArmario, resumen }.
 *
 * `resumen` trae los conteos de cada filtro sobre el armario COMPLETO,
 * no sobre lo filtrado, así los botones pueden mostrar "Gastadas (7)"
 * aunque estés viendo otra cosa.
 */
export function listarPrendas(filtros = {}) {
  const params = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== null && valor !== undefined && valor !== "") {
      params.set(clave, valor);
    }
  }
  const cola = params.toString();
  return pedir(`/api/prendas${cola ? `?${cola}` : ""}`);
}

/**
 * Sube las fotos y las hace clasificar. NO guarda ninguna prenda.
 * Devuelve { fichas } — una por foto, en el mismo orden que las mandaste.
 */
/* El idioma viaja para que el campo `nombre` —el único de texto libre
 * que devuelve la IA— salga en el idioma que la persona está viendo, y
 * no en español para los doce. El servidor lo pasa por lista blanca
 * (`vision.idiomaValido`): acá se manda lo que la app tiene puesto, no
 * se valida de este lado. */
export function analizarFotos(imagenes) {
  return pedir("/api/prendas/analizar", {
    method: "POST",
    body: JSON.stringify({ imagenes, idioma: idiomaActual() }),
  });
}

// Acepta una prenda o un lote. Guardar una es el caso particular de
// guardar N, no un camino aparte.
export function guardarPrendas(prendas) {
  return pedir("/api/prendas", {
    method: "POST",
    // El idioma, otra vez: si alguien borró el nombre a mano, el
    // respaldo que se GUARDA tiene que salir en su idioma.
    body: JSON.stringify({
      prendas: Array.isArray(prendas) ? prendas : [prendas],
      idioma: idiomaActual(),
    }),
  });
}

// Una sola prenda, completa. La ficha la usa en vez de reusar lo que
// ya tenga la pantalla que la abrió: cada pantalla maneja una versión
// recortada distinta del mismo dato, y editar sobre una recortada
// guardaría campos vacíos encima de datos buenos.
export function leerPrenda(id) {
  return pedir(`/api/prendas/${id}`);
}

/* Las fechas en que se usó esta prenda.
 *
 * Va SUELTA y no dentro de `leerPrenda`: la ficha se abre para editar,
 * y el historial solo se mira si el dueño lo despliega. Pegarlo a la
 * carga de la ficha haría pagar una consulta más a todo el mundo para
 * una sección que la mayoría no abre. */
export function usosDePrenda(id) {
  return pedir(`/api/prendas/${id}/usos`);
}

export function editarPrenda(id, cambios) {
  return pedir(`/api/prendas/${id}`, {
    method: "PATCH",
    body: JSON.stringify(cambios),
  });
}

/* ⚠️  NO MANDA NINGÚN id, Y ESO ES LA DEFENSA.
 *
 * El servidor borra la cuenta DEL TOKEN. Si esta función mandara un
 * user_id en el cuerpo, el servidor tendría que elegir entre creerle al
 * cuerpo o al token — y el día que alguien elija mal, con service_role
 * de por medio, es un botón para borrar la cuenta de cualquiera. */
export function borrarCuenta() {
  return pedir("/api/auth/cuenta", { method: "DELETE" });
}

export function borrarPrenda(id) {
  return pedir(`/api/prendas/${id}`, { method: "DELETE" });
}

// El borrado es blando (`activa = false`), así que deshacerlo es solo
// volver a prenderla. Sin esto, "borrar" sería definitivo desde la app
// y el único camino de vuelta sería el SQL editor de Supabase.
export function restaurarPrenda(id) {
  return editarPrenda(id, { activa: true });
}

// Sube una foto suelta, sin pasarla por la IA. Para reemplazar la foto
// de una prenda ya clasificada: no hace falta volver a preguntarle a
// Claude qué es algo que ya sabemos.
//
// `mini` es la versión de grilla que arma `prepararFoto`. Puede venir
// undefined —un navegador que no la pudo generar— y el servidor lo
// acepta: la prenda queda sin miniatura y la grilla usa la grande.
export function subirFoto({ base64, contentType, mini }) {
  return pedir("/api/prendas/foto", {
    method: "POST",
    body: JSON.stringify({ base64, contentType, mini: mini || null }),
  });
}

// Tira una foto ya subida que nunca llegó a ser prenda: sacaste esa
// ficha de la tanda antes de guardar. Sin esto quedaría ocupando lugar
// en Storage sin que ninguna fila la referencie.
// La miniatura se descarta junto con su foto: si no se mandara, quedaría
// un archivo huérfano por cada ficha que el usuario saca de la tanda.
export function descartarFoto(foto_path, foto_mini_path) {
  return pedir("/api/prendas/foto/descartar", {
    method: "DELETE",
    body: JSON.stringify({ foto_path, foto_mini_path: foto_mini_path || null }),
  });
}

/* ==================================================================
   Outfits
================================================================== */

// Pide combinaciones. Todo es opcional: sin filtros devuelve las mejores
// del armario entero.
export function sugerirOutfits({
  ocasion,
  estacion,
  ancla,
  incluirNoDisponibles,
  limite,
  sin_reglas,
  semilla,
  sinFichar,
} = {}) {
  const params = new URLSearchParams();
  if (ocasion) params.set("ocasion", ocasion);
  if (ancla) params.set("ancla", ancla);
  if (incluirNoDisponibles) params.set("incluir_no_disponibles", "1");
  if (limite) params.set("limite", String(limite));
  if (sin_reglas) params.set("sin_reglas", "1");
  // "Sin mostrar": armar solo con ropa que todavía no se fichó.
  if (sinFichar) params.set("sin_fichar", "1");
  if (semilla) params.set("semilla", String(semilla));
  // `estacion` hace las dos cosas que antes hacían dos parámetros: filtra
  // el armario y decide si hace falta abrigo. Se manda solo si hay una
  // elegida — sin ella el motor no filtra, que es lo correcto cuando no
  // se sabe dónde está el usuario.
  if (estacion) params.set("estacion", estacion);

  const cola = params.toString();
  return pedir(`/api/outfits/sugerencias${cola ? `?${cola}` : ""}`);
}

// Registra que usaste una combinación. Es lo que alimenta el orden por
// frescura: sin esto, todas las prendas son igual de "no usadas".
export function registrarOutfit({ prenda_ids, ocasion, nombre, usada_en, origen, perfume_id }) {
  return pedir("/api/outfits", {
    method: "POST",
    body: JSON.stringify({ prenda_ids, ocasion, nombre, usada_en, origen, perfume_id }),
  });
}

/* Veta un PAR de prendas, no un outfit. Es lo que hace que un solo
 * "no va" limpie cientos de combinaciones en vez de una.
 *
 * `prenda_ids` es la combinación COMPLETA desde la que vetaste, y no
 * sirve para el veto: sirve para la telemetría. El servidor no puede
 * deducirla —guarda hashes del conjunto entero, no ids— así que el par
 * solo nunca coincidiría con nada. Es opcional: vetar desde una
 * pantalla que no tiene una combinación a mano sigue funcionando.
 */
export function vetarPar(prenda_a, prenda_b, motivo, prenda_ids) {
  return pedir("/api/outfits/vetos", {
    method: "POST",
    body: JSON.stringify({ prenda_a, prenda_b, motivo, prenda_ids }),
  });
}

export function listarVetos() {
  return pedir("/api/outfits/vetos");
}

export function borrarVeto(prenda_a, prenda_b) {
  return pedir("/api/outfits/vetos", {
    method: "DELETE",
    body: JSON.stringify({ prenda_a, prenda_b }),
  });
}

// Analiza el armario y devuelve qué conviene comprar. Sin IA: es
// aritmética sobre combinaciones, así que no cuesta tokens.
export function queComprar(ocasion) {
  const cola = ocasion ? `?ocasion=${encodeURIComponent(ocasion)}` : "";
  return pedir(`/api/compras${cola}`);
}

export function listarOutfits() {
  return pedir("/api/outfits");
}

/**
 * Corregir un registro del historial: sus prendas, su fecha o su
 * ocasión. Se manda SOLO lo que cambió; lo que no viene no se toca.
 * Ver `PATCH /:id` en `lib/rutas_outfits.js`.
 */
export function editarOutfit({ id, prenda_ids, usada_en, ocasion, perfume_id }) {
  return pedir(`/api/outfits/${id}`, {
    method: "PATCH",
    // `undefined` se cae solo del JSON: lo que no cambió no viaja.
    // `null` en la ocasión o el perfume sí viaja, y es como se los saca.
    body: JSON.stringify({ prenda_ids, usada_en, ocasion, perfume_id }),
  });
}

export function borrarOutfit(id) {
  return pedir(`/api/outfits/${id}`, { method: "DELETE" });
}

/* ==================================================================
   Perfumes (22/9). Ver lib/rutas_perfumes.js.
================================================================== */

export function listarPerfumes() {
  return pedir("/api/perfumes");
}

export function crearPerfume({ nombre, marca, concentracion, familia, momento, ml, nivel, abierto_en }) {
  return pedir("/api/perfumes", {
    method: "POST",
    body: JSON.stringify({ nombre, marca, concentracion, familia, momento, ml, nivel, abierto_en }),
  });
}

/* Solo viaja lo que vino: `undefined` se cae del JSON y el servidor no
 * toca ese campo. `null` sí viaja, y es como se borra un dato. */
export function editarPerfume({ id, nombre, marca, concentracion, familia, momento, ml, nivel, abierto_en, activo }) {
  return pedir(`/api/perfumes/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ nombre, marca, concentracion, familia, momento, ml, nivel, abierto_en, activo }),
  });
}

export function borrarPerfume(id) {
  return pedir(`/api/perfumes/${id}`, { method: "DELETE" });
}

/** La IA propone marca, concentración, familia y momento. No guarda nada. */
export function datosDePerfume({ nombre, marca }) {
  return pedir("/api/perfumes/datos", {
    method: "POST",
    body: JSON.stringify({ nombre, marca }),
  });
}

export function subirFotoPerfume({ id, base64, contentType, mini }) {
  return pedir(`/api/perfumes/${id}/foto`, {
    method: "POST",
    body: JSON.stringify({ base64, contentType, mini: mini || null }),
  });
}

/**
 * Fichar —o desfichar— TODAS las prendas de una salida registrada.
 *
 * No se mandan los ids de las prendas a propósito: los deriva el
 * servidor de esa salida. Ver el comentario largo de la ruta en
 * `lib/rutas_outfits.js`.
 *
 * Devuelve `{ fichada, prendas: [ids] }` — los ids que quedaron
 * tocados, que pueden ser menos que los del outfit si alguna prenda se
 * borró del armario.
 */
export function ficharOutfit(id, fichada) {
  return pedir(`/api/outfits/${id}/fichar`, {
    method: "POST",
    body: JSON.stringify({ fichada }),
  });
}

/* ==================================================================
   Perfil
================================================================== */

/**
 * El perfil y las opciones que dependen de él.
 *
 * `guardarropa` se puede pasar para PREVISUALIZAR: la pantalla de
 * bienvenida necesita los ejemplos de estilo dirigidos antes de haber
 * guardado la respuesta, porque guarda todo junto al final.
 */
export function leerPerfil(previo = {}) {
  const cola = previo.guardarropa
    ? `?guardarropa=${encodeURIComponent(previo.guardarropa)}`
    : "";
  return pedir(`/api/perfil${cola}`);
}

/**
 * Marca la bienvenida como vista.
 *
 * Ruta propia y no un campo del perfil: la pantalla de Perfil manda el
 * perfil ENTERO al guardar, y una fecha en esa whitelist se reenviaría
 * y haría fallar el guardado de cualquier otro campo. Además, así la
 * fecha la pone el servidor — una que venga del navegador miente en
 * cuanto alguien tiene el reloj mal.
 */
export function marcarPrimerUso() {
  return pedir("/api/perfil/primer-uso", { method: "POST" });
}

export function guardarPerfil(cambios) {
  return pedir("/api/perfil", {
    method: "PATCH",
    body: JSON.stringify(cambios),
  });
}

/* ==================================================================
   Colores propios
================================================================== */

export function listarColores() {
  return pedir("/api/colores");
}

// Un color nuevo declara familia y si es neutro. No es burocracia: sin
// esas dos cosas la regla de estilo no sabe si choca con otro color, y
// el filtro del armario no sabe dónde agruparlo.
/* ⚠️  `hex` VA EN LA LISTA. FALTABA, Y ERA INVISIBLE (2026-08-24).
 *
 * NuevoColor.js pregunta "¿Cómo lo dibujo cuando no hay foto?", el
 * usuario elige una muestra, y acá se descartaba: esta función
 * destructuraba tres campos y `hex` no era uno. El POST salía sin él,
 * el servidor guardaba el color sin muestra, y el calendario y las
 * franjas lo dibujaban en trama gris PARA SIEMPRE.
 *
 * Indistinguible de "no se guardó" — que es el reporte que ya llegó
 * tres veces en este proyecto por otras causas.
 *
 * Es la sexta vez que una lista blanca apaga algo en silencio acá
 * (§14 colores, §15g nivelesExtra, §15j aPrendaDelMotor y salidaPrenda,
 * neutrosExtra en sugerencias). La regla, otra vez: UN CAMPO QUE FALTA
 * EN UNA LISTA BLANCA NO DA ERROR, DA VACÍO.
 */
export function crearColor({ nombre, familia, es_neutro, hex }) {
  return pedir("/api/colores", {
    method: "POST",
    body: JSON.stringify({ nombre, familia, es_neutro, hex }),
  });
}

export function borrarColor(clave, forzar) {
  return pedir(`/api/colores/${encodeURIComponent(clave)}${forzar ? "?forzar=1" : ""}`, {
    method: "DELETE",
  });
}

/* ==================================================================
   Ocasiones propias
================================================================== */

export function listarOcasiones() {
  return pedir("/api/ocasiones");
}

// Una ocasión nueva declara su NIVEL de formalidad (0-6). No es
// burocracia: la formalidad no es un campo de la prenda, se deduce de
// las ocasiones que tiene marcadas. Una ocasión sin nivel haría que la
// prenda se saltee la regla entera EN SILENCIO y combine con cualquier
// cosa — peor que un error, porque nadie lo notaría.
export function crearOcasion({ nombre, nivel }) {
  return pedir("/api/ocasiones", {
    method: "POST",
    body: JSON.stringify({ nombre, nivel }),
  });
}

// Armar la lista inicial de ocasiones a partir de un paquete.
//
// Se manda solo la clave del paquete: los nombres y los niveles los
// pone el servidor. Si el cliente mandara la lista armada, dos
// navegadores con versiones distintas sembrarían listas distintas —
// y la lista de ocasiones alimenta la regla de formalidad, así que
// serían reglas distintas.
export function sembrarOcasiones(paquete) {
  return pedir("/api/ocasiones/sembrar", {
    method: "POST",
    body: JSON.stringify({ paquete }),
  });
}

export function borrarOcasion(clave, forzar) {
  return pedir(
    `/api/ocasiones/${encodeURIComponent(clave)}${forzar ? "?forzar=1" : ""}`,
    { method: "DELETE" }
  );
}

/* ==================================================================
   Valija
================================================================== */

// POST y no GET aunque no escriba nada: los días de un viaje son una
// estructura ({Trabajo: 3, Casual: 2}) que en query string queda
// ilegible.
export function armarValija({ ocasiones, estacion, maxCalzados, semilla, prendas }) {
  return pedir("/api/valija", {
    method: "POST",
    body: JSON.stringify({ ocasiones, estacion, maxCalzados, semilla, prendas }),
  });
}

/* ------------------------------------------------------------------
   La valija guardada en la cuenta.

   Es UNA por usuario: no hay id, no hay lista. `guardarValija` hace
   upsert y `olvidarValija` la borra entera.

   Lo que se manda es lo mismo que vive en el borrador del navegador,
   así que las dos capas guardan la misma forma y no hay que traducir
   entre ellas. Ver src/Valija.js.
------------------------------------------------------------------ */

export function leerValijaGuardada() {
  return pedir("/api/valija/guardada");
}

export function guardarValija({ ocasiones, estacion, maxCalzados, elegidas, resultado }) {
  return pedir("/api/valija/guardada", {
    method: "PUT",
    body: JSON.stringify({ ocasiones, estacion, maxCalzados, elegidas, resultado }),
  });
}

export function olvidarValija() {
  return pedir("/api/valija/guardada", { method: "DELETE" });
}


